import React, { useState } from 'react';
import { Copy, Edit3, RefreshCw, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * One block of generated content (e.g. TikTok Caption, LinkedIn Post).
 * Props:
 *  - title: section label
 *  - icon: LucideIcon
 *  - text: current text value
 *  - onChange: (newText) => void
 *  - onRegenerate?: () => Promise<void>
 *  - testid
 */
const GeneratedContentCard = ({ title, icon: Icon, text, onChange, onRegenerate, testid }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text || '');
  const [regenerating, setRegenerating] = useState(false);
  const [justCopied, setJustCopied] = useState(false);

  React.useEffect(() => { setDraft(text || ''); }, [text]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text || '');
      setJustCopied(true);
      toast.success('Copié !');
      setTimeout(() => setJustCopied(false), 1500);
    } catch {
      toast.error('Impossible de copier');
    }
  };

  const handleRegen = async () => {
    if (!onRegenerate) return;
    setRegenerating(true);
    try { await onRegenerate(); } finally { setRegenerating(false); }
  };

  const save = () => { onChange?.(draft); setEditing(false); toast.success('Sauvegardé localement'); };

  return (
    <div className="rounded-xl border border-white/10 bg-[#0a0a0a] p-4 transition-colors hover:border-white/20" data-testid={testid}>
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {Icon && <Icon className="w-3.5 h-3.5 text-[#D4FF00] shrink-0" />}
          <span className="font-heading font-bold text-xs sm:text-sm uppercase tracking-wider truncate">{title}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {onRegenerate && (
            <button
              onClick={handleRegen}
              disabled={regenerating}
              className="p-1.5 rounded-md hover:bg-white/10 text-zinc-400 hover:text-white transition-colors disabled:opacity-50"
              title="Regenerate"
              data-testid={`${testid}-regenerate`}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${regenerating ? 'animate-spin' : ''}`} />
            </button>
          )}
          <button
            onClick={() => setEditing((v) => !v)}
            className={`p-1.5 rounded-md hover:bg-white/10 transition-colors ${editing ? 'text-[#D4FF00]' : 'text-zinc-400 hover:text-white'}`}
            title="Edit"
            data-testid={`${testid}-edit`}
          >
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={copy}
            className="p-1.5 rounded-md hover:bg-white/10 text-zinc-400 hover:text-white transition-colors"
            title="Copy"
            data-testid={`${testid}-copy`}
          >
            {justCopied ? <Check className="w-3.5 h-3.5 text-[#D4FF00]" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
      {editing ? (
        <div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={4}
            className="w-full bg-black border border-white/10 rounded-md p-3 text-sm text-zinc-200 focus:outline-none focus:border-[#D4FF00]/40 resize-y"
            data-testid={`${testid}-textarea`}
          />
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => { setDraft(text || ''); setEditing(false); }} className="text-xs text-zinc-500 hover:text-white px-2 py-1">Annuler</button>
            <button onClick={save} className="text-xs bg-[#D4FF00] text-black font-semibold px-3 py-1 rounded-md hover:bg-[#D4FF00]/90">Sauvegarder</button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap break-words">{text || <span className="text-zinc-600 italic">—</span>}</p>
      )}
    </div>
  );
};

export default GeneratedContentCard;
